// Group positioned tokens into reading-order lines. Column gaps are preserved
// as double spaces so the field/item layers can see the table structure even
// in the flattened text. (The LexVet positional-reconstruction inheritance.)

import type { Line, Token } from "./types";

function median(nums: number[]): number {
  if (!nums.length) return 0;
  const s = [...nums].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  const a = s[mid - 1] ?? s[0] ?? 0;
  const b = s[mid] ?? 0;
  return s.length % 2 ? b : (a + b) / 2;
}

export function groupLines(tokens: Token[]): Line[] {
  const lines: Line[] = [];
  const medH = median(tokens.map(t => t.h)) || 10;
  const yTol = Math.max(2, medH * 0.45);

  const pages = [...new Set(tokens.map(t => t.page))].sort((a, b) => a - b);
  for (const page of pages) {
    const pageTokens = tokens
      .filter(t => t.page === page)
      .sort((a, b) => a.y - b.y || a.x - b.x);

    let current: Token[] = [];
    let currentY = -1e9;
    const flush = () => {
      if (!current.length) return;
      const sorted = [...current].sort((a, b) => a.x - b.x);
      lines.push({ page, y: currentY, tokens: sorted, text: lineText(sorted, medH) });
      current = [];
    };
    for (const t of pageTokens) {
      if (Math.abs(t.y - currentY) > yTol) { flush(); currentY = t.y; }
      current.push(t);
    }
    flush();
  }
  return lines;
}

function lineText(sorted: Token[], medH: number): string {
  let out = "";
  let prevEnd = -1;
  for (const t of sorted) {
    if (prevEnd >= 0) {
      const gap = t.x - prevEnd;
      out += gap > medH * 1.2 ? "  " : " ";   // wide gap = column boundary
    }
    out += t.str.trim();
    prevEnd = t.x + t.w;
  }
  return out;
}

export function linesToText(lines: Line[]): string {
  let out = "";
  let prevPage = 0;
  let prevY = -1e9;
  for (const l of lines) {
    if (l.page !== prevPage) { if (prevPage) out += "\n\n— page " + l.page + " —\n\n"; prevPage = l.page; prevY = -1e9; }
    else if (l.y - prevY > 24) out += "\n";   // paragraph-ish gap
    out += l.text + "\n";
    prevY = l.y;
  }
  return out;
}
